import {
    useQuery,
    useMutation,
    useQueryClient,
    useIsMutating,
} from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { axiosInstance } from '../../helpers'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import { DateTime } from 'luxon'
import { useTimezone } from '../../contexts/timezone'

import {
    ezScheduleDetails,
    TemplateServiceVendor,
    TemplateService,
    TemplateUnit,
    CommonAreaRule,
    BathAreaRule,
} from '../../models'
import {
    UseEzScheduleOptions,
    UpdateEzScheduleParams,
    AddTemplateServiceParams,
    AddLineItemParams,
    AddTemplateVendorParams,
    UpdateTemplateServiceParams,
    DeleteTemplateServiceParams,
    UpdateTemplateVendorParams,
    DeleteTemplateVendorParams,
    UpdateTemplateServiceVendorParams,
    AddTemplateServiceVendorParams,
    DeleteTemplateServiceVendorParams,
    SetTemplateUnitsParams,
    GenerateWorkordersParams,
    PhaseScopedData,
    TemplateWorkordersByPhaseResponse,
    UnitCoverageByPhaseResponse,
    TemplateUnitsByPhaseResponse,
    TemplateSchedulesByPhaseResponse,
    DeleteLineItemParams,
    DuplicatableSchedulesResponse,
    DuplicateFromParams,
    DuplicateFromResponse,
} from './types'
import { AxiosResponse } from 'axios'

interface OptimisticUpdateContext {
    previousEzSchedule: { data: ezScheduleDetails } | undefined
}

interface UpdateTemplateServiceVendorContext extends OptimisticUpdateContext {
    templateServiceVendorId: number
    mutationVersion: number
}

type PhaseScopedResponse<T> = {
    phases: PhaseScopedData<T>[]
}

const isPhaseScopedArray = <T>(
    value: unknown,
): value is PhaseScopedData<T>[] => {
    if (!Array.isArray(value)) {
        return false
    }

    return value.every((phaseEntry) => {
        if (phaseEntry === null || typeof phaseEntry !== 'object') {
            return false
        }

        const entry = phaseEntry as {
            phase?: { id?: unknown }
            data?: unknown
        }

        return (
            entry.phase !== undefined &&
            typeof entry.phase?.id === 'number' &&
            'data' in entry
        )
    })
}

const isPhaseScopedResponse = <T>(
    value: unknown,
): value is PhaseScopedResponse<T> => {
    if (value === null || typeof value !== 'object') {
        return false
    }

    if (!('phases' in value)) {
        return false
    }

    const phases = (value as { phases: unknown }).phases
    if (!Array.isArray(phases)) {
        return false
    }

    return phases.every((phaseEntry) => {
        if (phaseEntry === null || typeof phaseEntry !== 'object') {
            return false
        }

        const entry = phaseEntry as {
            phase?: { id?: unknown }
            data?: unknown
        }

        return (
            entry.phase !== undefined &&
            typeof entry.phase?.id === 'number' &&
            'data' in entry
        )
    })
}

const toPhaseScopedList = <T>(
    value: T | PhaseScopedResponse<T> | PhaseScopedData<T>[] | undefined,
) => {
    if (isPhaseScopedResponse<T>(value)) {
        return value.phases
    }

    if (isPhaseScopedArray<T>(value)) {
        return value
    }

    return []
}

const getByPhaseData = <T>(value: T | PhaseScopedResponse<T> | undefined) => {
    return toPhaseScopedList(value)
}

const getSelectedPhaseData = <T>(
    value: T | PhaseScopedResponse<T> | PhaseScopedData<T>[] | undefined,
    selectedPhaseId?: number,
): T | undefined => {
    if (!value) {
        return undefined
    }

    const phases = toPhaseScopedList(value)
    if (phases.length === 0) {
        return value as T
    }

    if (selectedPhaseId === undefined) {
        return phases[0]?.data
    }

    const selectedPhase = phases.find(
        (phaseEntry) => phaseEntry.phase.id === selectedPhaseId,
    )

    return selectedPhase?.data ?? phases[0]?.data
}

export const useEzSchedule = (options: UseEzScheduleOptions) => {
    const queryClient = useQueryClient()
    const vendorMutationVersionsRef = useRef<Map<number, number>>(new Map())
    const {
        scheduleId,
        phaseId,
        enabled = true,
        includeTemplateSchedules = false,
    } = options
    const { currentTimezone } = useTimezone()
    const queriesEnabled = enabled && !!scheduleId
    const scheduleQueryOptions = {
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    } as const
    const ezScheduleQueryKey = ['ezSchedule', scheduleId] as const
    const templateWorkordersQueryKey = [
        'templateWorkorders',
        scheduleId,
    ] as const
    const unitCoverageQueryKey = ['unitCoverage', scheduleId] as const
    const templateSchedulesQueryKey = ['templateSchedules', scheduleId] as const
    const templateUnitsQueryKey = ['templateUnits', scheduleId] as const
    const duplicatableSchedulesQueryKey = [
        'duplicatableSchedules',
        scheduleId,
    ] as const

    const activeMutationCount = useIsMutating()

    // EZ Schedule

    const {
        data: ezSchedule,
        isLoading: ezScheduleLoading,
        isFetching: ezScheduleFetching,
        error: ezScheduleError,
        refetch: refetchEzSchedule,
    } = useQuery<AxiosResponse<ezScheduleDetails>>({
        queryKey: ezScheduleQueryKey,
        queryFn: () =>
            axiosInstance.get(`/workorder/ez-schedule/${scheduleId}/`),
        enabled: queriesEnabled,
        ...scheduleQueryOptions,
    })

    const updateEzSchedule = useMutation<
        AxiosResponse,
        Error,
        UpdateEzScheduleParams,
        OptimisticUpdateContext
    >({
        mutationFn: (params: UpdateEzScheduleParams) =>
            axiosInstance.patch(`/workorder/ez-schedule/${scheduleId}/`, {
                ...params,
                schedule_phase_id: phaseId,
            }),
        onMutate: async (params) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.removeQueries({ queryKey: templateWorkordersQueryKey })

            // Snapshot the previous value
            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)

            // Optimistically update the cache (day_off_list lives on Phase, not ezSchedule)
            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    const targetPhaseId =
                        phaseId ?? old.data.schedule_phases?.[0]?.id
                    const phaseToUpdate = old.data.phases.find(
                        (p) => p.phase.id === targetPhaseId,
                    )
                    let updatedDayOffList = phaseToUpdate?.day_off_list ?? []
                    const nowIso = new Date().toISOString()
                    const addDayOffDate = params.add_day_off_date
                        ? DateTime.fromISO(params.add_day_off_date)
                              .setZone(currentTimezone)
                              .startOf('day')
                        : null
                    const removeDayOffDate = params.remove_day_off_date
                        ? DateTime.fromISO(params.remove_day_off_date)
                              .setZone(currentTimezone)
                              .startOf('day')
                        : null

                    if (addDayOffDate) {
                        const exists = updatedDayOffList.some((dayOff) => {
                            const dayOffDate = DateTime.fromISO(dayOff.date)
                                .toUTC()
                                .startOf('day')
                            return dayOffDate.equals(addDayOffDate)
                        })

                        if (!exists) {
                            updatedDayOffList = [
                                ...updatedDayOffList,
                                {
                                    id: -Date.now(),
                                    schedule: scheduleId ?? null,
                                    template_service_vendor: null,
                                    created_date: nowIso,
                                    modified_date: nowIso,
                                    date: params.add_day_off_date ?? nowIso,
                                },
                            ]
                        }
                    }

                    if (removeDayOffDate) {
                        updatedDayOffList = updatedDayOffList.filter(
                            (dayOff) => {
                                const dayOffDate = DateTime.fromISO(dayOff.date)
                                    .setZone(currentTimezone)
                                    .startOf('day')
                                return !dayOffDate.equals(removeDayOffDate)
                            },
                        )
                    }

                    return {
                        ...old,
                        data: {
                            ...old.data,
                            common_area_rule:
                                (params.common_area_rule as CommonAreaRule) ??
                                old.data.common_area_rule,
                            bath_area_rule:
                                (params.bath_area_rule as BathAreaRule) ??
                                old.data.bath_area_rule,
                            use_multi_phase_turn:
                                params.use_multi_phase ??
                                old.data.use_multi_phase,
                            use_order_control:
                                params.use_order_control ??
                                old.data.use_order_control,

                            phases: old.data.phases.map((p) =>
                                p.phase.id === targetPhaseId
                                    ? {
                                          ...p,
                                          day_off_list: updatedDayOffList,
                                          transfer_start_date:
                                              params.transfer_start_date ===
                                              undefined
                                                  ? p.transfer_start_date
                                                  : params.transfer_start_date,
                                          transfer_end_date:
                                              params.transfer_end_date ===
                                              undefined
                                                  ? p.transfer_end_date
                                                  : params.transfer_end_date,
                                          dest_unit_ready_date:
                                              params.dest_unit_ready_date ===
                                              undefined
                                                  ? p.dest_unit_ready_date
                                                  : params.dest_unit_ready_date,
                                          dt_unit_ready_date:
                                              params.dt_unit_ready_date ===
                                              undefined
                                                  ? p.dt_unit_ready_date
                                                  : params.dt_unit_ready_date,
                                          work_start_date:
                                              params.work_start_date === null
                                                  ? null
                                                  : params.work_start_date
                                                  ? params.work_start_date.toISO()
                                                  : p.work_start_date,
                                          work_end_date:
                                              params.work_end_date === null
                                                  ? null
                                                  : params.work_end_date
                                                  ? params.work_end_date.toISO()
                                                  : p.work_end_date,
                                          template_is_valid: false,
                                      }
                                    : p,
                            ),
                        },
                    }
                },
            )

            // Return context with the previous value for rollback
            return { previousEzSchedule }
        },
        onError: (_err, _params, context) => {
            // Rollback to the previous value on error
            if (context?.previousEzSchedule) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure server state
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const addPhase = useMutation<
        AxiosResponse,
        Error,
        void,
        OptimisticUpdateContext
    >({
        mutationFn: () =>
            axiosInstance.post(
                `/workorder/ez-schedule/${scheduleId}/add-schedule-phase/`,
            ),
        onMutate: async () => {
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })

            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)

            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    const currentPhases = old.data.schedule_phases ?? []
                    const highestPhaseIndex = currentPhases.reduce(
                        (max, phase) =>
                            phase.phase_index > max ? phase.phase_index : max,
                        0,
                    )
                    const optimisticPhase = {
                        id: -Date.now(),
                        phase_index: highestPhaseIndex + 1,
                    }

                    return {
                        ...old,
                        data: {
                            ...old.data,
                            schedule_phases: [
                                ...currentPhases,
                                optimisticPhase,
                            ],
                        },
                    }
                },
            )

            return { previousEzSchedule }
        },
        onError: (_err, _params, context) => {
            if (context?.previousEzSchedule) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const removePhase = useMutation<
        AxiosResponse,
        Error,
        { phaseId: number },
        OptimisticUpdateContext
    >({
        mutationFn: ({ phaseId: targetPhaseId }) =>
            axiosInstance.delete(
                `/workorder/ez-schedule/${scheduleId}/remove-schedule-phase/`,
                { data: { schedule_phase_id: targetPhaseId } },
            ),
        onMutate: async ({ phaseId: targetPhaseId }) => {
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })

            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)

            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    return {
                        ...old,
                        data: {
                            ...old.data,
                            schedule_phases: old.data.schedule_phases.filter(
                                (phase) => phase.id !== targetPhaseId,
                            ),
                            phases: old.data.phases.filter(
                                (phase) => phase.phase.id !== targetPhaseId,
                            ),
                        },
                    }
                },
            )

            return { previousEzSchedule }
        },
        onError: (_err, _params, context) => {
            if (context?.previousEzSchedule) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.invalidateQueries({
                queryKey: templateWorkordersQueryKey,
            })
            queryClient.invalidateQueries({
                queryKey: unitCoverageQueryKey,
            })
        },
    })

    const resetPhase = useMutation<AxiosResponse, Error, { phaseId: number }>({
        mutationFn: ({ phaseId: targetPhaseId }) =>
            axiosInstance.post(
                `/workorder/ez-schedule/${scheduleId}/reset-schedule-phase/`,
                { schedule_phase_id: targetPhaseId },
            ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.invalidateQueries({
                queryKey: templateWorkordersQueryKey,
            })
            queryClient.invalidateQueries({
                queryKey: unitCoverageQueryKey,
            })
            queryClient.invalidateQueries({
                queryKey: templateUnitsQueryKey,
            })
        },
    })

    const [
        isGeneratingTemplateWorkorders,
        setIsGeneratingTemplateWorkorders,
    ] = useState(false)

    const {
        data: templateWorkorders,
        isLoading: templateWorkordersLoading,
        isFetching: templateWorkordersFetching,
        error: templateWorkordersError,
        refetch: refetchTemplateWorkorders,
    } = useQuery<AxiosResponse<TemplateWorkordersByPhaseResponse>>({
        queryKey: templateWorkordersQueryKey,
        queryFn: () =>
            axiosInstance.get(`/workorder/template-workorder/`, {
                params: {
                    schedule_id: scheduleId,
                },
            }),
        // Disable while generating so observers cannot refetch a mid-write snapshot.
        enabled: queriesEnabled && !isGeneratingTemplateWorkorders,
        ...scheduleQueryOptions,
    })

    const {
        data: unitCoverage,
        isLoading: unitCoverageLoading,
        error: unitCoverageError,
        refetch: refetchUnitCoverage,
    } = useQuery<AxiosResponse<UnitCoverageByPhaseResponse>>({
        queryKey: unitCoverageQueryKey,
        queryFn: () =>
            axiosInstance.get(
                `/workorder/template-workorder/get-unit-coverage/`,
                {
                    params: {
                        schedule_id: scheduleId,
                    },
                },
            ),
        enabled: queriesEnabled && !isGeneratingTemplateWorkorders,
        ...scheduleQueryOptions,
    })

    const generateWorkorders = useMutation<
        AxiosResponse,
        Error,
        GenerateWorkordersParams
    >({
        mutationFn: (params: GenerateWorkordersParams) =>
            axiosInstance.post(
                `/workorder/ez-schedule/${scheduleId}/generate-workorders/`,
                {
                    schedule_phase_ids: params.phaseIds,
                    delete_existing_workorders:
                        params.deleteExistingWorkorders ?? false,
                    creation_source: params.creationSource,
                },
            ),
        onSuccess: () => {
            // Ideally we would invalidate the schedule query
            queryClient.invalidateQueries({
                queryKey: templateWorkordersQueryKey,
            })
            queryClient.invalidateQueries({
                queryKey: unitCoverageQueryKey,
            })
        },
    })

    const generateTemplateWorkorders = useMutation<AxiosResponse, Error, void>({
        mutationFn: () =>
            axiosInstance.post(
                `/workorder/ez-schedule/${scheduleId}/generate-template-workorders/`,
                { schedule_phase_id: phaseId },
            ),
        onMutate: async () => {
            // Block observer refetches for the duration of generation. Do NOT
            // removeQueries — that remounts active observers and can fetch a
            // partial mid-generation snapshot (first service only).
            setIsGeneratingTemplateWorkorders(true)
            await queryClient.cancelQueries({
                queryKey: templateWorkordersQueryKey,
            })
            await queryClient.cancelQueries({
                queryKey: unitCoverageQueryKey,
            })

            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        data: {
                            ...old.data,
                            phases: old.data.phases.map((phase) =>
                                phaseId === undefined ||
                                phase.phase.id === phaseId
                                    ? { ...phase, template_is_valid: false }
                                    : phase,
                            ),
                        },
                    }
                },
            )
        },
        onSuccess: async () => {
            // fetchQuery works even while the observer query is disabled.
            await queryClient.fetchQuery({
                queryKey: templateWorkordersQueryKey,
                queryFn: () =>
                    axiosInstance.get(`/workorder/template-workorder/`, {
                        params: { schedule_id: scheduleId },
                    }),
            })
            await queryClient.fetchQuery({
                queryKey: unitCoverageQueryKey,
                queryFn: () =>
                    axiosInstance.get(
                        `/workorder/template-workorder/get-unit-coverage/`,
                        { params: { schedule_id: scheduleId } },
                    ),
            })

            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        data: {
                            ...old.data,
                            phases: old.data.phases.map((phase) =>
                                phaseId === undefined ||
                                phase.phase.id === phaseId
                                    ? { ...phase, template_is_valid: true }
                                    : phase,
                            ),
                        },
                    }
                },
            )
        },
        onError: (err) => {
            const axiosErr = err as AxiosError<{ error?: string }>
            toast.error(
                axiosErr.response?.data?.error ??
                    'Could not generate template workorders.',
            )
        },
        onSettled: () => {
            setIsGeneratingTemplateWorkorders(false)
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const {
        data: templateSchedules,
        isLoading: templateSchedulesLoading,
        error: templateSchedulesError,
        refetch: refetchTemplateSchedules,
    } = useQuery<
        AxiosResponse<TemplateSchedulesByPhaseResponse | ezScheduleDetails[]>
    >({
        queryKey: templateSchedulesQueryKey,
        queryFn: () =>
            axiosInstance.get(
                `/workorder/ez-schedule/${scheduleId}/get-template-schedules/`,
            ),
        enabled: queriesEnabled && includeTemplateSchedules,
        ...scheduleQueryOptions,
    })

    const {
        data: duplicatableSchedulesResponse,
        isLoading: duplicatableSchedulesLoading,
        error: duplicatableSchedulesError,
        refetch: refetchDuplicatableSchedules,
    } = useQuery<AxiosResponse<DuplicatableSchedulesResponse>>({
        queryKey: duplicatableSchedulesQueryKey,
        queryFn: () =>
            axiosInstance.get(
                `/workorder/ez-schedule/${scheduleId}/duplicatable-schedules/`,
            ),
        enabled: queriesEnabled,
        ...scheduleQueryOptions,
    })

    const duplicateFromSchedule = useMutation({
        mutationFn: (params: DuplicateFromParams) =>
            axiosInstance.post<DuplicateFromResponse>(
                `/workorder/ez-schedule/${scheduleId}/duplicate-from/`,
                params,
            ),
        onSuccess: (response) => {
            // Seed cache before Auto Scheduler mounts so unit selection
            // initializes from included template units (not stale empty setup).
            queryClient.setQueryData<AxiosResponse<ezScheduleDetails>>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) {
                        return {
                            data: response.data.schedule,
                        } as AxiosResponse<ezScheduleDetails>
                    }
                    return {
                        ...old,
                        data: response.data.schedule,
                    }
                },
            )
            queryClient.invalidateQueries({ queryKey: ezScheduleQueryKey })
            queryClient.invalidateQueries({
                queryKey: duplicatableSchedulesQueryKey,
            })
            queryClient.invalidateQueries({
                queryKey: templateWorkordersQueryKey,
            })
        },
    })

    // Updating Underlying EZ Schedule Models
    // When these are modfied, we need to invalidate the current set of template workorders
    // We also need to optimistically update the ezSchedule cache to reflect the changes these will cause

    // Template Services

    const addTemplateService = useMutation<
        AxiosResponse,
        Error,
        AddTemplateServiceParams,
        OptimisticUpdateContext
    >({
        mutationFn: (params: AddTemplateServiceParams) =>
            axiosInstance.post(`/workorder/template-service/`, {
                service: params.serviceId,
                schedule_id: params.scheduleId,
                portfolio: params.portfolio,
                schedule_phase_id: phaseId,
            }),
        onMutate: async (params) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.removeQueries({ queryKey: templateWorkordersQueryKey })

            // Snapshot the previous value
            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)

            // Create a temporary optimistic entry with a negative ID
            const tempId = -Date.now()
            const now = new Date().toISOString()

            // Optimistically update the cache by adding the new service
            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    const appendService = (
                        services: TemplateService[],
                    ): TemplateService[] => {
                        const alreadyExists = services.some(
                            (ts) => ts.service.id === params.serviceId,
                        )
                        if (alreadyExists) return services

                        const optimisticService: TemplateService = {
                            id: tempId,
                            created_date: now,
                            modified_date: now,
                            schedule: params.scheduleId,
                            service: {
                                id: params.serviceId,
                                name: params.serviceName,
                                color: '#999999',
                                order: services.length,
                            },
                            position: services.length,
                            start_date: '',
                            end_date: '',
                            template_service_vendors: [],
                            budget: 0,
                            forecast: 0,
                            variance: 0,
                            invoice: 0,
                            min_start_date: null,
                            stand_alone: false,
                        }

                        return [...services, optimisticService]
                    }

                    return {
                        ...old,
                        data: {
                            ...old.data,

                            phases: old.data.phases.map((phase) => ({
                                ...phase,
                                template_services: appendService(
                                    phase.template_services,
                                ),
                                template_is_valid: false,
                            })),
                        },
                    }
                },
            )

            // Return context with the previous value for rollback
            return { previousEzSchedule }
        },
        onError: (_err, _params, context) => {
            // Rollback to the previous value on error
            if (context?.previousEzSchedule) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure server state
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const updateTemplateService = useMutation<
        AxiosResponse,
        Error,
        UpdateTemplateServiceParams,
        OptimisticUpdateContext
    >({
        mutationFn: (params: UpdateTemplateServiceParams) =>
            axiosInstance.patch(
                `/workorder/template-service/${params.templateServiceId}/`,
                {
                    budget: params.budget,
                    stand_alone: params.stand_alone,
                    position: params.position,
                    schedule_phase_id: phaseId,
                    auto_select_areas: params.auto_select_areas,
                },
            ),
        onMutate: async (params) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.removeQueries({ queryKey: templateWorkordersQueryKey })

            // Snapshot the previous value
            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)

            // Optimistically update the cache
            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    // If position is being updated, reorder the services
                    if (params.position !== undefined) {
                        const reorder = (
                            services: TemplateService[],
                        ): TemplateService[] => {
                            const result = [...services]
                            const idx = result.findIndex(
                                (ts) => ts.id === params.templateServiceId,
                            )
                            if (idx === -1) return services
                            const [moved] = result.splice(idx, 1)
                            result.splice(params.position!, 0, moved)
                            return result
                        }

                        return {
                            ...old,
                            data: {
                                ...old.data,

                                phases: old.data.phases.map((phase) => ({
                                    ...phase,
                                    template_services: reorder(
                                        phase.template_services,
                                    ),
                                    template_is_valid: false,
                                })),
                            },
                        }
                    }

                    const updateFields = (
                        services: TemplateService[],
                    ): TemplateService[] =>
                        services.map((ts) => {
                            if (ts.id !== params.templateServiceId) return ts
                            return {
                                ...ts,
                                stand_alone:
                                    params.stand_alone ?? ts.stand_alone,
                                budget: params.budget ?? ts.budget,
                                auto_select_areas:
                                    params.auto_select_areas ??
                                    ts.auto_select_areas,
                            }
                        })

                    return {
                        ...old,
                        data: {
                            ...old.data,
                            template_is_valid: false,
                            phases: old.data.phases.map((phase) => ({
                                ...phase,
                                template_services: updateFields(
                                    phase.template_services,
                                ),
                                template_is_valid: false,
                            })),
                        },
                    }
                },
            )

            // Return context with the previous value for rollback
            return { previousEzSchedule }
        },
        onError: (_err, _params, context) => {
            // Rollback to the previous value on error
            if (context?.previousEzSchedule) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure server state
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const deleteTemplateService = useMutation<
        AxiosResponse,
        Error,
        DeleteTemplateServiceParams,
        OptimisticUpdateContext
    >({
        mutationFn: (params: DeleteTemplateServiceParams) =>
            axiosInstance.delete(
                `/workorder/template-service/${params.templateServiceId}/`,
                {
                    params: {
                        schedule_phase_id: phaseId,
                    },
                },
            ),
        onMutate: async (params) => {
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.removeQueries({ queryKey: templateWorkordersQueryKey })

            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)

            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    const removeService = (
                        services: TemplateService[],
                    ): TemplateService[] =>
                        services.filter(
                            (ts) => ts.id !== params.templateServiceId,
                        )

                    return {
                        ...old,
                        data: {
                            ...old.data,
                            phases: old.data.phases.map((phase) => ({
                                ...phase,
                                template_services: removeService(
                                    phase.template_services,
                                ),
                                template_is_valid: false,
                            })),
                        },
                    }
                },
            )

            return { previousEzSchedule }
        },
        onError: (_err, _params, context) => {
            if (context?.previousEzSchedule) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    // Template Service Vendors

    const addTemplateServiceVendor = useMutation<
        AxiosResponse,
        Error,
        AddTemplateServiceVendorParams,
        OptimisticUpdateContext
    >({
        mutationFn: (params: AddTemplateServiceVendorParams) =>
            axiosInstance.post(`/workorder/template-service-vendor/`, {
                template_service: params.templateServiceId,
                vendor: params.vendorId,
                auto_invoice: params.auto_invoice ?? false,
                schedule_phase_id: phaseId,
            }),
        onMutate: async (params) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.removeQueries({ queryKey: templateWorkordersQueryKey })

            // Snapshot the previous value
            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)

            // Create a temporary optimistic entry with a negative ID
            const tempId = -Date.now()
            const now = new Date().toISOString()
            const optimisticVendor: TemplateServiceVendor = {
                id: tempId,
                template_service: params.templateServiceId,
                vendor: {
                    id: params.vendorId,
                    name: params.vendorName,
                },
                capacity: 0,
                day_off_list: [],
                start_date: '',
                end_date: '',
                auto_invoice: params.auto_invoice ?? false,
                created_date: now,
                modified_date: now,
                assigned_unit: [],
                use_max_price: false,
            }

            // Optimistically update the cache by adding the new vendor
            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    const appendVendor = (
                        services: TemplateService[],
                    ): TemplateService[] =>
                        services.map((ts) => {
                            if (ts.id === params.templateServiceId) {
                                return {
                                    ...ts,
                                    template_service_vendors: [
                                        ...ts.template_service_vendors,
                                        optimisticVendor,
                                    ],
                                }
                            }
                            return ts
                        })

                    return {
                        ...old,
                        data: {
                            ...old.data,
                            template_is_valid: false,
                            phases: old.data.phases.map((phase) => ({
                                ...phase,
                                template_services: appendVendor(
                                    phase.template_services,
                                ),
                                template_is_valid: false,
                            })),
                        },
                    }
                },
            )

            // Return context with the previous value for rollback
            return { previousEzSchedule }
        },
        onError: (_err, _params, context) => {
            // Rollback to the previous value on error
            if (context?.previousEzSchedule) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure server state
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const updateTemplateServiceVendor = useMutation<
        AxiosResponse,
        Error,
        UpdateTemplateServiceVendorParams,
        UpdateTemplateServiceVendorContext
    >({
        mutationKey: ['updateTemplateServiceVendor', scheduleId],
        mutationFn: (params: UpdateTemplateServiceVendorParams) =>
            axiosInstance.patch(
                `/workorder/template-service-vendor/${params.templateServiceVendorId}/`,
                {
                    start_date: params.start_date,
                    end_date: params.end_date,
                    auto_invoice: params.auto_invoice,
                    units: params.units,
                    schedule_phase_id: phaseId,
                },
            ),
        onMutate: async (params) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.removeQueries({ queryKey: templateWorkordersQueryKey })

            // Snapshot the previous value
            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)
            const currentVersion =
                vendorMutationVersionsRef.current.get(
                    params.templateServiceVendorId,
                ) ?? 0
            const nextVersion = currentVersion + 1
            vendorMutationVersionsRef.current.set(
                params.templateServiceVendorId,
                nextVersion,
            )

            // Optimistically update the cache
            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    const updateTemplateServiceVendors = (
                        templateServices: TemplateService[],
                    ): TemplateService[] => {
                        return templateServices.map((ts) => ({
                            ...ts,
                            template_service_vendors: ts.template_service_vendors.map(
                                (tsv) => {
                                    if (
                                        tsv.id !==
                                        params.templateServiceVendorId
                                    ) {
                                        return tsv
                                    }

                                    return {
                                        ...tsv,
                                        start_date:
                                            params.start_date ?? tsv.start_date,
                                        end_date:
                                            params.end_date ?? tsv.end_date,
                                        auto_invoice:
                                            params.auto_invoice ??
                                            tsv.auto_invoice,
                                    }
                                },
                            ),
                        }))
                    }

                    return {
                        ...old,
                        data: {
                            ...old.data,
                            template_is_valid: false,
                            phases: old.data.phases.map((phase) => ({
                                ...phase,
                                template_services: updateTemplateServiceVendors(
                                    phase.template_services,
                                ),
                                template_is_valid: false,
                            })),
                        },
                    }
                },
            )

            // Return context with the previous value for rollback
            return {
                previousEzSchedule,
                templateServiceVendorId: params.templateServiceVendorId,
                mutationVersion: nextVersion,
            }
        },
        onError: (_err, _params, context) => {
            // Rollback to the previous value on error
            if (!context?.previousEzSchedule) {
                return
            }

            const currentVersion = vendorMutationVersionsRef.current.get(
                context.templateServiceVendorId,
            )

            // Only rollback if this mutation is still the latest for this vendor.
            if (currentVersion === context.mutationVersion) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: (_data, _err, _params, context) => {
            if (context) {
                const currentVersion = vendorMutationVersionsRef.current.get(
                    context.templateServiceVendorId,
                )

                if (currentVersion === context.mutationVersion) {
                    vendorMutationVersionsRef.current.delete(
                        context.templateServiceVendorId,
                    )
                }
            }

            // Avoid refetch churn while rapid arrow clicks are still mutating.
            // Invalidate once the final in-flight vendor-update mutation settles.
            const inFlightVendorUpdates = queryClient.isMutating({
                mutationKey: ['updateTemplateServiceVendor', scheduleId],
            })

            if (inFlightVendorUpdates === 1) {
                queryClient.invalidateQueries({
                    queryKey: ezScheduleQueryKey,
                })
            }
        },
    })

    const deleteTemplateServiceVendor = useMutation<
        AxiosResponse,
        Error,
        DeleteTemplateServiceVendorParams,
        OptimisticUpdateContext
    >({
        mutationFn: (params: DeleteTemplateServiceVendorParams) =>
            axiosInstance.delete(
                `/workorder/template-service-vendor/${params.templateServiceVendorId}/`,
                {
                    params: {
                        schedule_phase_id: phaseId,
                    },
                },
            ),
        onMutate: async (params) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.removeQueries({ queryKey: templateWorkordersQueryKey })

            // Snapshot the previous value
            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)

            // Optimistically update the cache by filtering out the deleted vendor
            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    const removeVendor = (
                        services: TemplateService[],
                    ): TemplateService[] =>
                        services.map((ts) => ({
                            ...ts,
                            template_service_vendors: ts.template_service_vendors.filter(
                                (tsv) =>
                                    tsv.id !== params.templateServiceVendorId,
                            ),
                        }))

                    return {
                        ...old,
                        data: {
                            ...old.data,
                            template_is_valid: false,
                            phases: old.data.phases.map((phase) => ({
                                ...phase,
                                template_services: removeVendor(
                                    phase.template_services,
                                ),
                                template_is_valid: false,
                            })),
                        },
                    }
                },
            )

            // Return context with the previous value for rollback
            return { previousEzSchedule }
        },
        onError: (_err, _params, context) => {
            // Rollback to the previous value on error
            if (context?.previousEzSchedule) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure server state
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    // Template Units

    const {
        data: templateUnits,
        isLoading: templateUnitsLoading,
        error: templateUnitsError,
        refetch: refetchTemplateUnits,
    } = useQuery<AxiosResponse<TemplateUnitsByPhaseResponse | TemplateUnit[]>>({
        queryKey: templateUnitsQueryKey,
        queryFn: () =>
            axiosInstance.get(`/workorder/template-unit/${scheduleId}/`),
        enabled: queriesEnabled,
        ...scheduleQueryOptions,
    })

    const setTemplateUnits = useMutation<
        AxiosResponse,
        Error,
        SetTemplateUnitsParams,
        OptimisticUpdateContext
    >({
        mutationFn: (params: SetTemplateUnitsParams) => {
            if (phaseId === undefined) {
                throw new Error(
                    'Cannot set template units without a selected schedule phase.',
                )
            }

            return axiosInstance.post(
                `/workorder/template-unit/${scheduleId}/`,
                {
                    ordered_units: params.ordered_units,
                    schedule_phase_id: phaseId,
                },
            )
        },
        onMutate: async (params) => {
            await queryClient.cancelQueries({
                queryKey: ezScheduleQueryKey,
            })
            queryClient.removeQueries({ queryKey: templateWorkordersQueryKey })

            const previousEzSchedule = queryClient.getQueryData<{
                data: ezScheduleDetails
            }>(ezScheduleQueryKey)

            const orderedUnitMap = new Map(
                params.ordered_units.map((unit) => [unit.unitId, unit.order]),
            )
            const selectedUnitIds = new Set(
                params.ordered_units.map((unit) => unit.unitId),
            )

            queryClient.setQueryData<{ data: ezScheduleDetails }>(
                ezScheduleQueryKey,
                (old) => {
                    if (!old) return old

                    const sortTemplateUnits = (
                        templateUnits: TemplateUnit[],
                    ) => {
                        const updatedUnits = templateUnits.map((unit) => {
                            const nextOrder = orderedUnitMap.get(unit.unit.id)
                            const isIncluded = selectedUnitIds.has(unit.unit.id)
                            return {
                                ...unit,
                                is_included: isIncluded,
                                order: nextOrder ?? unit.order,
                            }
                        })

                        return [...updatedUnits].sort(
                            (a, b) => a.order - b.order,
                        )
                    }

                    if (phaseId !== undefined) {
                        const updatedPhases = old.data.phases.map((phase) => {
                            if (phase.phase.id !== phaseId) {
                                return phase
                            }

                            return {
                                ...phase,
                                template_is_valid: false,
                                template_units: sortTemplateUnits(
                                    phase.template_units,
                                ),
                            }
                        })

                        return {
                            ...old,
                            data: {
                                ...old.data,
                                template_is_valid: false,
                                phases: updatedPhases,
                            },
                        }
                    }

                    const sortedUnits = sortTemplateUnits(
                        old.data.template_units,
                    )

                    return {
                        ...old,
                        data: {
                            ...old.data,
                            template_is_valid: false,
                            template_units: sortedUnits,
                        },
                    }
                },
            )

            return { previousEzSchedule }
        },
        onError: (_err, _params, context) => {
            if (context?.previousEzSchedule) {
                queryClient.setQueryData(
                    ezScheduleQueryKey,
                    context.previousEzSchedule,
                )
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: templateUnitsQueryKey,
            })
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    // Modifying Related Models to EZ Schedule
    const addLineItem = useMutation({
        mutationFn: (params: AddLineItemParams) =>
            axiosInstance.post(`/workorder/schedule-budget-item/`, {
                schedule_id: params.scheduleId,
                description: params.description,
                cost: params.cost,
                schedule_phase_id: phaseId,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const deleteLineItem = useMutation({
        mutationFn: (params: DeleteLineItemParams) =>
            axiosInstance.delete(
                `/workorder/schedule-budget-item/${params.lineItemId}/`,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const addTemplateVendor = useMutation({
        mutationFn: (params: AddTemplateVendorParams) =>
            axiosInstance.post(`/workorder/template-vendor/`, {
                vendor: params.vendorId,
                schedule_id: params.scheduleId,
                portfolio: params.portfolio,
                schedule_phase_id: phaseId,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const updateTemplateVendor = useMutation({
        mutationFn: (params: UpdateTemplateVendorParams) =>
            axiosInstance.patch(
                `/workorder/template-vendor/${params.templateVendorId}/`,
                {
                    budget: params.budget,
                    start_date: params.start_date,
                    end_date: params.end_date,
                    units: params.units,
                    schedule_phase_id: phaseId,
                },
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const deleteTemplateVendor = useMutation({
        mutationFn: (params: DeleteTemplateVendorParams) =>
            axiosInstance.delete(
                `/workorder/template-vendor/${params.templateVendorId}/`,
                {
                    params: {
                        schedule_phase_id: phaseId,
                    },
                },
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ezScheduleQueryKey,
            })
        },
    })

    const selectedPhaseId =
        phaseId ?? ezSchedule?.data?.schedule_phases?.[0]?.id

    const templateWorkordersByPhase = templateWorkorders?.data?.phases ?? []
    const unitCoverageByPhase = unitCoverage?.data?.phases ?? []
    const templateUnitsByPhase = getByPhaseData<TemplateUnit[]>(
        templateUnits?.data,
    )
    const templateSchedulesByPhase = getByPhaseData<ezScheduleDetails[]>(
        templateSchedules?.data,
    )

    const selectedPhaseTemplateWorkorders =
        selectedPhaseId === undefined
            ? templateWorkordersByPhase[0]?.template_workorders ?? []
            : templateWorkordersByPhase.find(
                  (phaseEntry) => phaseEntry.phase.id === selectedPhaseId,
              )?.template_workorders ?? []

    const selectedPhaseUnitCoverage =
        selectedPhaseId === undefined
            ? unitCoverageByPhase[0]?.unit_coverage
            : unitCoverageByPhase.find(
                  (phaseEntry) => phaseEntry.phase.id === selectedPhaseId,
              )?.unit_coverage

    const selectedPhaseTemplateUnits =
        getSelectedPhaseData<TemplateUnit[]>(
            templateUnits?.data,
            selectedPhaseId,
        ) ?? []

    const selectedPhaseTemplateSchedules =
        getSelectedPhaseData<ezScheduleDetails[]>(
            templateSchedules?.data,
            selectedPhaseId,
        ) ?? []

    const selectedPhaseTemplateIsValid =
        selectedPhaseId === undefined
            ? ezSchedule?.data?.phases[0]?.template_is_valid ?? null
            : ezSchedule?.data?.phases.find(
                  (phase) => phase.phase.id === selectedPhaseId,
              )?.template_is_valid ?? null

    const selectedPhaseTemplateInputRevision =
        selectedPhaseId === undefined
            ? ezSchedule?.data?.phases[0]?.template_input_revision ?? null
            : ezSchedule?.data?.phases.find(
                  (phase) => phase.phase.id === selectedPhaseId,
              )?.template_input_revision ?? null

    const selectedPhaseTemplateRevision =
        selectedPhaseId === undefined
            ? ezSchedule?.data?.phases[0]?.template_revision ?? null
            : ezSchedule?.data?.phases.find(
                  (phase) => phase.phase.id === selectedPhaseId,
              )?.template_revision ?? null

    return {
        ezSchedule: ezSchedule?.data as ezScheduleDetails,
        ezScheduleLoading,
        ezScheduleFetching: ezScheduleFetching || activeMutationCount > 0,
        ezScheduleError: ezScheduleError ?? null,
        refetchEzSchedule,
        updateEzSchedule: updateEzSchedule.mutate,
        updateEzScheduleAsync: updateEzSchedule.mutateAsync,
        templateIsValid: selectedPhaseTemplateIsValid,
        templateInputRevision: selectedPhaseTemplateInputRevision,
        templateRevision: selectedPhaseTemplateRevision,
        templateWorkorders: selectedPhaseTemplateWorkorders,
        templateWorkordersByPhase,
        templateWorkordersLoading:
            templateWorkordersLoading ||
            templateWorkordersFetching ||
            isGeneratingTemplateWorkorders,
        templateWorkordersError,
        refetchTemplateWorkorders,
        refetchUnitCoverage,
        generateWorkorders: (
            phaseIds: number[],
            deleteExistingWorkorders: boolean,
            creationSource: string,
        ) =>
            generateWorkorders.mutateAsync({
                phaseIds,
                deleteExistingWorkorders,
                creationSource,
            }),
        addTemplateService: addTemplateService.mutate,
        updateTemplateService: updateTemplateService.mutate,
        deleteTemplateService: deleteTemplateService.mutate,
        addLineItem: addLineItem.mutate,
        deleteLineItem: deleteLineItem.mutate,
        addTemplateVendor: addTemplateVendor.mutate,
        updateTemplateVendor: updateTemplateVendor.mutate,
        deleteTemplateVendor: deleteTemplateVendor.mutate,
        addTemplateServiceVendor: addTemplateServiceVendor.mutate,
        deleteTemplateServiceVendor: deleteTemplateServiceVendor.mutate,
        updateTemplateServiceVendor: updateTemplateServiceVendor.mutate,
        templateUnits: selectedPhaseTemplateUnits,
        templateUnitsByPhase,
        setTemplateUnits: setTemplateUnits.mutateAsync,
        generateTemplateWorkorders: generateTemplateWorkorders.mutate,
        generateTemplateWorkordersLoading:
            generateTemplateWorkorders.isLoading ||
            isGeneratingTemplateWorkorders,
        templateSchedules: selectedPhaseTemplateSchedules,
        templateSchedulesByPhase,
        templateSchedulesLoading: templateSchedulesLoading,
        templateSchedulesError: templateSchedulesError,
        refetchTemplateSchedules: refetchTemplateSchedules,
        unitCoverage: selectedPhaseUnitCoverage,
        unitCoverageByPhase,
        addPhase: addPhase.mutateAsync,
        addPhaseLoading: addPhase.isLoading,
        removePhase: removePhase.mutateAsync,
        removePhaseLoading: removePhase.isLoading,
        resetPhase: resetPhase.mutateAsync,
        resetPhaseLoading: resetPhase.isLoading,
        duplicatableSchedules:
            duplicatableSchedulesResponse?.data?.schedules ?? [],
        targetHasSetup:
            duplicatableSchedulesResponse?.data?.target_has_setup ?? false,
        duplicatableSchedulesLoading,
        duplicatableSchedulesError: duplicatableSchedulesError ?? null,
        refetchDuplicatableSchedules,
        duplicateFromSchedule: duplicateFromSchedule.mutateAsync,
        duplicateFromScheduleLoading: duplicateFromSchedule.isLoading,
    }
}
